import React, { useEffect, useRef, useState, forwardRef } from 'react';
import Hls from 'hls.js';
import Icon from '../images/Icon';
import styles from './SimpleHlsPlayer.module.scss';

interface SimpleHlsPlayerProps {
  streamUrl : string;
  active?   : boolean;
  className?: string;
  onClick?  : () => void;
  logo?     : string;
}

// ── forwardRef ────────────────────────────────────────────────────────────────
// Quand un ref externe est fourni (ex: depuis BroadcastingChannelCharacteristics
// pour le brancher sur HlsAudioPanel), on l'utilise directement.
// Dans ce cas on ne force JAMAIS video.muted = true — c'est HlsAudioPanel
// qui gère le gain via AudioContext.
// Sans ref externe, le comportement est identique à l'original (muted par défaut).

export const SimpleHlsPlayer = forwardRef<HTMLVideoElement, SimpleHlsPlayerProps>(({
  streamUrl,
  active    = true,
  className = '',
  onClick,
  logo,
}, forwardedRef) => {

  const internalRef          = useRef<HTMLVideoElement | null>(null);
  const hlsRef               = useRef<Hls | null>(null);
  const reconnectIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const stallTimeoutRef      = useRef<NodeJS.Timeout | null>(null);
  const videoListenersRef    = useRef<{
    waiting?   : () => void;
    playing?   : () => void;
    ended?     : () => void;
    error?     : () => void;
    loadeddata?: () => void;
  } | null>(null);
  const [isConnected,     setIsConnected]     = useState(false);
  const [hasBeenConnected, setHasBeenConnected] = useState(false);
  const [playError,       setPlayError]       = useState(false);

  // Résolution du ref : externe si fourni, sinon interne
  const videoRef = (forwardedRef ?? internalRef) as React.MutableRefObject<HTMLVideoElement | null>;

  // Quand un ref externe est fourni, HlsAudioPanel gère le son via AudioContext.
  // On ne doit pas toucher à video.muted dans ce cas.
  const hasExternalRef = forwardedRef !== null && forwardedRef !== undefined;

  const cleanupHls = () => {
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    if (stallTimeoutRef.current) {
      clearTimeout(stallTimeoutRef.current);
      stallTimeoutRef.current = null;
    }
    if (videoRef.current && videoListenersRef.current) {
      const v  = videoRef.current;
      const ls = videoListenersRef.current;
      if (ls.waiting)    v.removeEventListener('waiting',    ls.waiting);
      if (ls.playing)    v.removeEventListener('playing',    ls.playing);
      if (ls.ended)      v.removeEventListener('ended',      ls.ended);
      if (ls.error)      v.removeEventListener('error',      ls.error);
      if (ls.loadeddata) v.removeEventListener('loadeddata', ls.loadeddata);
      videoListenersRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.src = '';
    }
  };

  const tryConnect = () => {
    if (!videoRef.current || !streamUrl || !active) return;

    setPlayError(false);
    const video = videoRef.current;

    cleanupHls();

    if (Hls.isSupported()) {
      const hls = new Hls({
        maxLiveSyncPlaybackRate : 1.0,
        enableWorker            : true,
        lowLatencyMode          : true,
        manifestLoadingTimeOut  : 10000,
        manifestLoadingMaxRetry : 4,
        manifestLoadingRetryDelay: 1000,
        levelLoadingTimeOut     : 10000,
        levelLoadingMaxRetry    : 4,
        fragLoadingTimeOut      : 20000,
        fragLoadingMaxRetry     : 6,
        fragLoadingRetryDelay   : 1000,
      });
      hlsRef.current = hls;

      hls.on(Hls.Events.ERROR, (evt, data) => {
        if (process.env.NODE_ENV === 'development') {
          console.error('Erreur HLS:', data.type, data.details, data.fatal);
        }
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              setIsConnected(false);
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              try { hls.recoverMediaError(); }
              catch { setIsConnected(false); cleanupHls(); }
              break;
            default:
              setIsConnected(false);
              cleanupHls();
          }
        } else if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          if (data.details === 'manifestLoadError' || data.details === 'manifestLoadTimeOut') {
            setIsConnected(false);
          }
        }
      });

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setIsConnected(true);
        setHasBeenConnected(true);
        // ── Compatibilité HlsAudioPanel ──────────────────────────────────────
        // Si un ref externe est fourni, HlsAudioPanel crée un AudioContext sur
        // cette <video> et contrôle le gain lui-même. Forcer muted=true ici
        // n'empêche pas l'AudioContext de fonctionner mais coupe la sortie
        // native — on laisse HlsAudioPanel décider.
        // Sans ref externe : comportement original, muted pour l'autoplay.
        if (!hasExternalRef) {
          video.muted = true;
        }
        video.play().catch((error) => {
          if (process.env.NODE_ENV === 'development') {
            console.error('Erreur lors de la lecture automatique:', error);
          }
          setPlayError(true);
        });
      });

      const onWaiting = () => {
        stallTimeoutRef.current = setTimeout(() => setIsConnected(false), 10000);
      };
      const onPlaying = () => {
        if (stallTimeoutRef.current) { clearTimeout(stallTimeoutRef.current); stallTimeoutRef.current = null; }
      };
      const onEnded = () => { setIsConnected(false); cleanupHls(); };
      const onError = () => { setIsConnected(false); cleanupHls(); };
      videoListenersRef.current = { waiting: onWaiting, playing: onPlaying, ended: onEnded, error: onError };
      video.addEventListener('waiting', onWaiting);
      video.addEventListener('playing', onPlaying);
      video.addEventListener('ended',   onEnded);
      video.addEventListener('error',   onError);

      hls.attachMedia(video);
      hls.loadSource(streamUrl);

    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      const onLoadeddata = () => { setIsConnected(true); setHasBeenConnected(true); };
      const onError      = () => setIsConnected(false);
      const onEnded      = () => setIsConnected(false);
      videoListenersRef.current = { loadeddata: onLoadeddata, error: onError, ended: onEnded };

      video.src = streamUrl;
      if (!hasExternalRef) video.muted = true;

      video.addEventListener('loadeddata', onLoadeddata);
      video.addEventListener('error',      onError);
      video.addEventListener('ended',      onEnded);

      video.play().catch(() => setPlayError(true));

    } else {
      if (process.env.NODE_ENV === 'development') {
        console.error('HLS non supporté sur ce navigateur');
      }
      setIsConnected(false);
    }
  };

  useEffect(() => {
    if (!active) {
      if (reconnectIntervalRef.current) {
        clearInterval(reconnectIntervalRef.current);
        reconnectIntervalRef.current = null;
      }
      setIsConnected(false);
      setHasBeenConnected(false);
      cleanupHls();
      return;
    }

    if (isConnected) {
      if (reconnectIntervalRef.current) {
        clearInterval(reconnectIntervalRef.current);
        reconnectIntervalRef.current = null;
      }
    } else {
      tryConnect();
      if (reconnectIntervalRef.current) clearInterval(reconnectIntervalRef.current);
      reconnectIntervalRef.current = setInterval(() => tryConnect(), 5000);
    }

    return () => {
      if (reconnectIntervalRef.current) {
        clearInterval(reconnectIntervalRef.current);
        reconnectIntervalRef.current = null;
      }
    };
  }, [active, isConnected, streamUrl]);

  useEffect(() => {
    return () => {
      if (reconnectIntervalRef.current) {
        clearInterval(reconnectIntervalRef.current);
        reconnectIntervalRef.current = null;
      }
      cleanupHls();
    };
  }, []);

  return (
    <div className={`${styles.playerContainer} ${className}`} onClick={onClick}>
      {!hasBeenConnected && !isConnected && (
        <div className={styles.errorContainer}>
          {logo
            ? <img src={logo} alt="" style={{ maxWidth: '80%', maxHeight: '80%', width: 'auto', height: 'auto', objectFit: 'contain' }} />
            : <Icon name      = "sleepingTV"
                    width     = {100}
                    height    = {100}
                    color     = "white"
                    hoverable = {false}
                    clickable = {false} />
          }
        </div>
      )}
      <video
        ref={videoRef}
        className={`${styles.video} ${!hasBeenConnected ? styles.hidden : ''}`}
        playsInline
        // Pas d'attribut muted statique quand un ref externe est fourni :
        // HlsAudioPanel démute lui-même la vidéo dans son connect().
        muted={!hasExternalRef}
        aria-label="Lecture du flux vidéo"
        aria-live="polite"
      />
      {playError && (
        <button
          className={styles.playButton}
          onClick={() => videoRef.current?.play()}
        >
          Cliquez pour lire
        </button>
      )}
    </div>
  );
});

SimpleHlsPlayer.displayName = 'SimpleHlsPlayer';

export default SimpleHlsPlayer;